/**
 * Catalog Content Component
 * Fetches and displays approved items from Firestore
 */

'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { db } from '@/lib/firebase.client';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import FavoriteButton from '@/components/FavoriteButton';

interface OgData {
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImage?: string | null;
  ogUrl?: string | null;
  ogSiteName?: string | null;
  favicon?: string | null;
}

interface CatalogItem {
  id: string;
  title: string;
  description: string;
  category?: string;
  accountType: 'FSA' | 'HSA' | 'Both';
  productUrl?: string;
  ogData?: OgData;
  availableUntil?: string | number | Timestamp | null;
  reasoning?: string;
  createdAt?: Timestamp;
}

// Helper to parse date from various formats
function parseDate(date: string | number | Timestamp | null | undefined): Date | null {
  if (!date) return null;
  
  if (typeof date === 'number') {
    return new Date(date);
  } else if (typeof date === 'string') {
    return new Date(date);
  } else if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    return date.toDate();
  }
  
  return null;
}

export default function CatalogContent() {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'All' | 'FSA' | 'HSA' | 'Both'>('All');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      // Fetch approved submissions from Firestore
      const submissionsRef = collection(db, 'submissions');
      const q = query(
        submissionsRef,
        where('status', '==', 'approved'),
        orderBy('approvedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const now = new Date();
      
      const fetchedItems: CatalogItem[] = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        
        // Check if item is still available (respect availableUntil date)
        if (data.availableUntil) {
          const availableUntilDate = parseDate(data.availableUntil);
          if (availableUntilDate && availableUntilDate < now) {
            return; // Skip expired items
          }
        }
        
        fetchedItems.push({
          id: docSnap.id,
          title: data.title,
          description: data.description,
          category: data.category,
          accountType: data.accountType || 'Both',
          productUrl: data.productUrl,
          ogData: data.ogData,
          availableUntil: data.availableUntil,
          reasoning: data.reasoning,
          createdAt: data.createdAt,
        });
      });

      setItems(fetchedItems);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching catalog items:', error);
      setLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    const matchesFilter = filter === 'All' || item.accountType === filter || item.accountType === 'Both';
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    return matchesFilter && matchesSearch;
  });

  const isExpiringSoon = (availableUntil: string | number | Timestamp | null | undefined) => {
    const date = parseDate(availableUntil);
    if (!date) return false;
    const daysUntilExpiry = Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };
  
  const formatAvailableDate = (availableUntil: string | number | Timestamp | null | undefined) => {
    const date = parseDate(availableUntil);
    if (!date) return '';
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Item Catalog</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse our community-sourced database of FSA and HSA eligible items
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search items..."
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
          />
          <div className="flex gap-2 flex-wrap">
            {(['All', 'FSA', 'HSA', 'Both'] as const).map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={`px-6 py-3 rounded-xl font-semibold transition-colors ${
                  filter === filterOption
                    ? 'bg-primary-500 text-white'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-primary-500'
                }`}
              >
                {filterOption}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500"></div>
            <p className="text-gray-600 mt-4">Loading catalog...</p>
          </div>
        )}

        {/* Items Grid */}
        {!loading && filteredItems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <Link
                key={item.id}
                href={`/catalog/${item.id}`}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl hover:border-primary-200 transition-all group"
              >
                {/* Image */}
                {item.ogData?.ogImage && (
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={item.ogData.ogImage}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                    {isExpiringSoon(item.availableUntil) && (
                      <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        Expiring Soon!
                      </div>
                    )}
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <h2 className="text-xl font-bold text-gray-900 flex-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h2>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <FavoriteButton
                        itemId={item.id}
                        title={item.title}
                        description={item.description}
                        image={item.ogData?.ogImage || undefined}
                        classification="YES"
                      />
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          item.accountType === 'Both'
                            ? 'bg-purple-100 text-purple-700'
                            : item.accountType === 'FSA'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {item.accountType}
                      </span>
                    </div>
                  </div>
                  
                  {item.category && (
                    <p className="text-sm text-primary-600 font-medium mb-3">{item.category}</p>
                  )}
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{item.description}</p>
                  
                  {item.ogData?.ogSiteName && (
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                      {item.ogData.favicon && (
                        <img src={item.ogData.favicon} alt="" className="w-4 h-4" onError={(e) => (e.currentTarget.style.display = 'none')} />
                      )}
                      <span>{item.ogData.ogSiteName}</span>
                    </div>
                  )}
                  
                  {item.availableUntil && (
                    <p className="text-xs text-orange-600 font-medium">
                      Available until {formatAvailableDate(item.availableUntil)}
                    </p>
                  )}
                  
                  <div className="mt-4 text-primary-600 font-semibold text-sm group-hover:text-primary-700">
                    View Details →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No items found</h2>
            <p className="text-gray-600 mb-6">
              {items.length === 0 
                ? "No approved items yet. Be the first to submit!" 
                : "Try adjusting your search or filter"
              }
            </p>
            {items.length > 0 ? (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilter('All');
                }}
                className="bg-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
              >
                Clear Filters
              </button>
            ) : (
              <Link
                href="/submit"
                className="inline-block bg-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
              >
                Submit an Item
              </Link>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Don&apos;t see what you&apos;re looking for?</h2>
          <p className="text-gray-700 mb-6">
            Help us grow the catalog by submitting items you know are FSA/HSA eligible.
          </p>
          <Link
            href="/submit"
            className="inline-block bg-primary-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
          >
            Submit an Item →
          </Link>
        </div>
      </div>
    </div>
  );
}
